import React, { useState, useEffect, useCallback } from 'react';
import {
  ClockIcon,
  CalendarIcon,
  ChartBarIcon,
  UserGroupIcon,
  ArrowTrendingUpIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

// DDD 구조 임포트
import { useAttendanceData } from '../use-cases';
import { formatTime } from '../utils';

/**
 * 통계/리포트 페이지
 * - 이용 통계 (총 등록자, 평균 세션 시간, 피크 시간대)
 * - 이용권별 현황
 *
 * 실시간 출석 현황은 출석부(/owner/seat-operation)에서 확인
 * 입출입 기록은 입출입 기록(/owner/entrance-exit)에서 확인
 */
const AttendanceManagement: React.FC = () => {
  const [lastUpdate, setLastUpdate] = useState<string>('');
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  // 출결 데이터 훅 (통계만 사용)
  const {
    stats,
    loading,
    error,
    fetchStats,
  } = useAttendanceData();

  // 초기 데이터 로드
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // 수동 새로고침 핸들러
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    setLastUpdate(new Date().toLocaleTimeString('ko-KR'));
    try {
      await fetchStats();
    } finally {
      setTimeout(() => setIsRefreshing(false), 500);
    }
  }, [fetchStats]);

  // 로딩 상태
  if (loading && !stats.last_updated) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* 헤더 */}
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">통계 / 리포트</h1>
            <p className="text-gray-600">
              이용 현황 통계와 분석 리포트를 확인합니다
            </p>
          </div>

          {/* 새로고침 버튼 */}
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className={`flex items-center px-4 py-2 rounded-lg transition-all ${
              isRefreshing
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-blue-500 text-white hover:bg-blue-600'
            }`}
            title="통계 새로고침"
          >
            <ArrowTrendingUpIcon
              className={`h-5 w-5 mr-2 ${isRefreshing ? 'animate-spin' : ''}`}
            />
            <span className="hidden sm:inline">새로고침</span>
          </button>
        </div>

        {lastUpdate && (
          <p className="text-sm text-gray-500 mt-2">
            마지막 업데이트: {lastUpdate}
          </p>
        )}
        {error && (
          <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-yellow-800 text-sm">{error}</p>
          </div>
        )}
      </div>

      {/* 준비 중 안내 */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start">
          <ExclamationTriangleIcon className="h-5 w-5 text-blue-500 mt-0.5 mr-3" />
          <div>
            <h3 className="text-sm font-medium text-blue-800">통계 기능 준비 중</h3>
            <p className="text-sm text-blue-700 mt-1">
              상세 통계 및 리포트 기능이 곧 추가될 예정입니다.
              현재는 기본 통계만 표시됩니다.
            </p>
          </div>
        </div>
      </div>

      {/* 통계 카드들 */}
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <UserGroupIcon className="h-8 w-8 text-blue-500" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">총 등록자</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.total_users}명
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ClockIcon className="h-8 w-8 text-green-500" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">평균 세션 시간</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatTime(stats.average_session_minutes)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ChartBarIcon className="h-8 w-8 text-purple-500" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">피크 시간대</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.peak_hours.length > 0
                      ? `${stats.peak_hours[0].hour_display} (${stats.peak_hours[0].user_count}명)`
                      : '-'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CalendarIcon className="h-8 w-8 text-orange-500" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">오늘 체크인</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.today_checkins}명
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 이용권별 현황 */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              이용권별 현황
            </h3>
            {Object.keys(stats.membership_breakdown).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(stats.membership_breakdown).map(([type, count]) => (
                  <div
                    key={type}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <span className="text-sm font-medium text-gray-900">{type}</span>
                    <span className="text-lg font-bold text-gray-900">{count}명</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">
                이용권 데이터가 없습니다
              </p>
            )}
          </div>
        </div>

        {/* 추후 추가될 기능 미리보기 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-gray-100 shadow rounded-lg p-6 opacity-60">
            <h3 className="text-lg font-medium text-gray-700 mb-2">📊 일별 이용 추이</h3>
            <p className="text-sm text-gray-500">준비 중...</p>
            <div className="mt-4 h-32 bg-gray-200 rounded flex items-center justify-center">
              <span className="text-gray-400">차트 영역</span>
            </div>
          </div>

          <div className="bg-gray-100 shadow rounded-lg p-6 opacity-60">
            <h3 className="text-lg font-medium text-gray-700 mb-2">📈 시간대별 이용률</h3>
            <p className="text-sm text-gray-500">준비 중...</p>
            <div className="mt-4 h-32 bg-gray-200 rounded flex items-center justify-center">
              <span className="text-gray-400">차트 영역</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceManagement;
